import React, { useEffect, useState, useCallback } from 'react';
import { collection, query, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import Header from './Header';
import Footer from './Footer';
import TransactionCards from './TransactionCards';
import useCredits from '../hooks/useCredits';
import { db } from '../firebase';
import './TransactionHistoryPage.css';

const PAGE_SIZE = 20;

function TransactionHistoryPage({ user, signIn }) {
  const { credits, loading: creditsLoading } = useCredits(user);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);

  const fetchPage = useCallback(async (afterDoc) => {
    if (!user) return;
    const txnRef = collection(db, 'users', user.uid, 'transactions');
    const constraints = [orderBy('created_at', 'desc'), limit(PAGE_SIZE + 1)];
    if (afterDoc) constraints.splice(1, 0, startAfter(afterDoc));
    const q = query(txnRef, ...constraints);
    const snap = await getDocs(q);
    const docs = snap.docs;
    const hasNext = docs.length > PAGE_SIZE;
    const pageDocs = hasNext ? docs.slice(0, PAGE_SIZE) : docs;
    return {
      items: pageDocs.map((d) => ({ id: d.id, ...d.data() })),
      lastDoc: pageDocs[pageDocs.length - 1] || null,
      hasMore: hasNext,
    };
  }, [user]);

  useEffect(() => {
    if (!user) { setTransactions([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    fetchPage(null).then((result) => {
      setTransactions(result.items);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
      setLoading(false);
    }).catch((err) => {
      console.error('Error fetching transactions:', err);
      setError("We couldn't load your transaction history. Please try again.");
      setLoading(false);
    });
  }, [user, fetchPage]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const result = await fetchPage(lastDoc);
      setTransactions((prev) => [...prev, ...result.items]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Error loading more transactions:', err);
      setLoadMoreError("Couldn't load more transactions. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="txn-history-page">
      <Header user={user} signIn={signIn} credits={credits} creditsLoading={creditsLoading} />
      <div className="txn-history-content">
        <h1>Transaction History</h1>

        {!user ? (
          <div className="txn-history-signin">
            <p>Sign in to view your transaction history.</p>
            <button className="btn-teal" onClick={signIn}>Sign In</button>
          </div>
        ) : loading ? (
          <p className="txn-history-muted">Loading transactions...</p>
        ) : error ? (
          <p className="txn-history-error" role="alert">{error}</p>
        ) : transactions.length === 0 ? (
          <p className="txn-history-muted">No transactions yet. Purchases and credit adjustments will show up here.</p>
        ) : (
          <>
            <table className="transaction-table txn-history-desktop">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => (
                  <tr key={txn.id}>
                    <td>{formatDate(txn.created_at)}</td>
                    <td>{txn.type}</td>
                    <td>{txn.description}</td>
                    <td className={(txn.amount || 0) >= 0 ? 'txn-positive' : 'txn-negative'}>
                      {(txn.amount || 0) >= 0 ? '+' : ''}{txn.amount || 0}
                    </td>
                    <td>{txn.balance_after || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TransactionCards transactions={transactions} formatDate={formatDate} />
            {loadMoreError && (
              <p className="txn-history-error" role="alert">{loadMoreError}</p>
            )}
            {hasMore && (
              <div className="txn-history-load-more">
                <button
                  className="btn-load-more"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default TransactionHistoryPage;
